import React from "react";
import { Stack, Typography } from "@mui/material";

import { HubIcon } from "../HubIcon";
import { HubContextMenu } from "../HubContextMenu";
import {
  getAttachmentDownloadUrl,
  useDeleteAttachment,
} from "../../hooks/react-query/useAttachmentsData";
import { getCategoryLabel } from "../../constants/attachments";

const formatBytes = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isPreviewable = (mimeType) =>
  /^application\/pdf$/i.test(mimeType) || /^image\//i.test(mimeType);

const ACTIONS = {
  PREVIEW: "preview",
  DOWNLOAD: "download",
  DELETE: "delete",
};

const AttachmentList = ({ attachments = [], allowDelete = true }) => {
  const { mutate: deleteAttachment, isLoading } = useDeleteAttachment();

  if (!attachments.length) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ py: "4px" }}>
        No files yet.
      </Typography>
    );
  }

  return (
    <Stack gap="6px">
      {attachments.map((a) => {
        const previewUrl = getAttachmentDownloadUrl(a.id, { inline: true });
        const downloadUrl = getAttachmentDownloadUrl(a.id);
        const categoryMeta = getCategoryLabel(a.category);

        const menuItems = [
          isPreviewable(a.mimeType) && {
            id: ACTIONS.PREVIEW,
            label: "Preview",
            icon: "Eye",
            href: previewUrl,
          },
          {
            id: ACTIONS.DOWNLOAD,
            label: "Download",
            icon: "Download",
            href: downloadUrl,
            download: a.fileName,
          },
          allowDelete && {
            id: ACTIONS.DELETE,
            label: "Delete",
            icon: "Trash2",
            color: "redError",
          },
        ].filter(Boolean);

        const onMenuSelect = (item) => {
          if (item.id === ACTIONS.DELETE) {
            deleteAttachment(a.id);
            return;
          }
          const link = document.createElement("a");
          link.href = item.href;
          if (item.download) link.download = item.download;
          if (item.id === ACTIONS.PREVIEW) {
            link.target = "_blank";
            link.rel = "noreferrer";
          }
          link.click();
        };

        return (
          <Stack
            key={a.id}
            direction="row"
            alignItems="center"
            gap="8px"
            sx={{
              padding: "6px 10px",
              border: "1px solid",
              borderColor: "hubColors.greyLight",
              borderRadius: "6px",
              "&:hover": { backgroundColor: "hubColors.greyLightest" },
            }}
          >
            <HubIcon icon={categoryMeta?.icon || "File"} size="14px" />
            <Stack flex={1} sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                fontSize={12}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {a.fileName}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontSize={10}>
                {formatBytes(a.fileSize)}
                {a.uploadedBy?.name ? ` · ${a.uploadedBy.name}` : ""}
              </Typography>
            </Stack>

            <HubContextMenu
              id={`attachment-${a.id}`}
              menuItems={menuItems}
              onSelect={onMenuSelect}
              loading={isLoading}
              HubIconProps={{ size: 14 }}
              IconButtonProps={{ sx: { p: "2px" } }}
            />
          </Stack>
        );
      })}
    </Stack>
  );
};

export default AttachmentList;
